import React, { useRef, useState } from "react";
import { Camera, Share2, Loader2, X } from "lucide-react";

const COLORS = ["#c9a24a", "#8a6cae", "#5a8c72", "#d4af37", "#b06a8e"];

/**
 * ViralSnapshot — "Capture the Moment": client-side only, no outside integration.
 * Draws a 5-second canvas animation (confetti + reveal + GIFTED watermark) and records it via
 * MediaRecorder into a downloadable/shareable .webm clip. Independent of the on-screen DOM confetti.
 */
const ViralSnapshot = ({ occasion = "", cashAmount = null, charityName = null }) => {
  const [recording, setRecording] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [error, setError] = useState("");
  const urlRef = useRef("");

  const capture = () => {
    setError(""); setVideoUrl(""); setRecording(true);
    const canvas = document.createElement("canvas");
    canvas.width = 540; canvas.height = 540;
    const ctx = canvas.getContext("2d");
    const particles = Array.from({ length: 70 }, () => ({
      x: Math.random() * 540, y: -Math.random() * 540, r: 4 + Math.random() * 5,
      c: COLORS[Math.floor(Math.random() * COLORS.length)],
      vy: 2 + Math.random() * 4, vx: (Math.random() - 0.5) * 2, rot: Math.random() * 360, vr: (Math.random() - 0.5) * 8,
    }));

    let stream;
    try { stream = canvas.captureStream(30); } catch (e) {
      setError("Video capture isn't supported on this browser."); setRecording(false); return;
    }
    let recorder;
    try { recorder = new MediaRecorder(stream, { mimeType: "video/webm;codecs=vp9" }); }
    catch (e) {
      try { recorder = new MediaRecorder(stream, { mimeType: "video/webm" }); }
      catch (e2) { setError("Video capture isn't supported on this browser."); setRecording(false); return; }
    }

    const chunks = [];
    recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setVideoUrl(url);
      setRecording(false);
    };
    recorder.start();

    const start = performance.now();
    const duration = 5000;
    const draw = (t) => {
      const elapsed = t - start;
      const grad = ctx.createLinearGradient(0, 0, 540, 540);
      grad.addColorStop(0, "#1b1330"); grad.addColorStop(1, "#3a1f3d");
      ctx.fillStyle = grad; ctx.fillRect(0, 0, 540, 540);

      ctx.textAlign = "center";
      ctx.font = "28px Georgia"; ctx.fillStyle = "#f5e9c8";
      ctx.fillText("🍾 A Royal Gift 🍾", 270, 130);
      if (occasion) { ctx.font = "20px Georgia"; ctx.fillStyle = "#e8d9b0"; ctx.fillText(occasion, 270, 168); }
      if (cashAmount) { ctx.font = "bold 46px Georgia"; ctx.fillStyle = "#f3d78a"; ctx.fillText(`$${Number(cashAmount).toFixed(2)}`, 270, 290); }
      else if (charityName) { ctx.font = "22px Georgia"; ctx.fillStyle = "#f3d78a"; ctx.fillText(charityName, 270, 290); }

      particles.forEach((p) => {
        p.y += p.vy; p.x += p.vx; p.rot += p.vr;
        if (p.y > 560) { p.y = -10; p.x = Math.random() * 540; }
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate((p.rot * Math.PI) / 180);
        ctx.fillStyle = p.c; ctx.fillRect(-p.r / 2, -p.r / 2, p.r, p.r * 1.6);
        ctx.restore();
      });

      ctx.textAlign = "right";
      ctx.font = "bold 16px Georgia"; ctx.fillStyle = "rgba(255,255,255,0.55)";
      ctx.fillText("GIFTED", 520, 520);

      if (elapsed < duration) requestAnimationFrame(draw);
      else recorder.stop();
    };
    requestAnimationFrame(draw);
  };

  const shareVideo = async () => {
    if (!videoUrl) return;
    try {
      const blob = await fetch(videoUrl).then((r) => r.blob());
      const file = new File([blob], "gifted-moment.webm", { type: "video/webm" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "GIFTED — A Royal Moment" });
        return;
      }
    } catch (e) { console.error("Native share failed, falling back to download:", e); }
    const a = document.createElement("a"); a.href = videoUrl; a.download = "gifted-moment.webm"; a.click();
  };

  return (
    <div data-testid="viral-snapshot" className="mt-4">
      {!videoUrl ? (
        <button data-testid="capture-moment-btn" onClick={capture} disabled={recording}
          className="w-full flex items-center justify-center gap-2 surface rounded-2xl py-3 text-primary hover:border-primary/40 disabled:opacity-60">
          {recording ? <><Loader2 className="w-4 h-4 animate-spin" /> Capturing… (5s)</> : <><Camera className="w-4 h-4" /> Capture the Moment</>}
        </button>
      ) : (
        <div className="surface rounded-2xl p-3" data-testid="snapshot-preview">
          <video src={videoUrl} controls loop className="w-full rounded-xl mb-2" data-testid="snapshot-video" />
          <div className="flex gap-2">
            <button data-testid="snapshot-share-btn" onClick={shareVideo}
              className="flex-1 flex items-center justify-center gap-1.5 text-sm bg-gradient-to-r from-primary to-secondary text-white rounded-xl py-2 hover:opacity-90"><Share2 className="w-4 h-4" /> Share / Save</button>
            <button onClick={() => setVideoUrl("")} className="flex-1 flex items-center justify-center gap-1.5 text-sm surface rounded-xl py-2 text-muted-foreground"><X className="w-4 h-4" /> Retake</button>
          </div>
        </div>
      )}
      {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
    </div>
  );
};

export default ViralSnapshot;
